/*
 * � 2025, NTT Data Group. All rights reserved. See LICENSE.txt file in the project root for third party licenses info.
 */

import { Injectable } from "@angular/core";
import { catchError, map, Observable, of } from "rxjs";
import { LineChart } from "../../models/http/charts/line-chart/line-chart.model";
import { CarbonIntensitySummary } from "../../models/http/what-if/carbon-intensity/carbon-intensity-summary.model";
import { CommunicationService } from "../communication.service";
import { MapChart } from "../../models/http/charts/map-chart/map-chart.model";
import { ChartResponse } from "../../models/ui/charts.model";
import { HttpMethods } from "../../models/ui/http-methods.model";
import { ENDPOINT_GROUPS } from "../../utils/apis/apis.constants";

@Injectable({
	providedIn: "root"
})
export class WhatIfCarbonIntensityHttpService {
	private _whatIfCarbonIntensityOperations = ENDPOINT_GROUPS.whatIfCarbonIntensity.operations;

	constructor(private _communicationService: CommunicationService) {}

	/**
	 * Retrieves all carbon intensity data.
	 *
	 * @returns An Observable that emits an object containing an array of CarbonIntensityData.
	 */
	public getAllCarbonIntensity(): Observable<ChartResponse<MapChart>> {
		return this._communicationService
			.callService(
				this._whatIfCarbonIntensityOperations.getAllCarbonIntensity.service,
				HttpMethods.GET
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as MapChart, hasError: true }))
			);
	}

	/**
	 * Retrieves the monthly average carbon intensity for a specified region.
	 *
	 * @param {string} region - The name of the region for which to get the carbon intensity monthly average.
	 * @returns An observable containing the monthly average carbon intensity data.
	 */
	public getMonthlyAverageByRegion(region: string): Observable<CarbonIntensitySummary> {
		const pathParams = { region };

		return this._communicationService.callService(
			this._whatIfCarbonIntensityOperations.getMonthlyAverageByRegion.service,
			HttpMethods.GET,
			{ pathParams }
		);
	}

	/**
	 * Retrieves the hourly average carbon intensity for a specified region.
	 *
	 * @param {string} region - The region for which to get the carbon intensity hourly average.
	 * @returns An observable containing the line chart data for the region's carbon intensity hourly average.
	 */
	public getHourlyAverageByRegion(region: string): Observable<ChartResponse<LineChart>> {
		const pathParams = { region };

		return this._communicationService
			.callService(
				this._whatIfCarbonIntensityOperations.getHourlyAverageByRegion.service,
				HttpMethods.GET,
				{ pathParams }
			)
			.pipe(
				map(response => ({ chart: response, hasError: false })),
				catchError(() => of({ chart: {} as LineChart, hasError: true }))
			);
	}
}
